/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGetOperatorsQuery } from "@/services/access-automation";
import { useFindUserInLogixQuery } from "@/services/logix";
import { CardLoadingSpinner } from "components/loading/CardLoadingSpinner";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FormData } from "./validate-form";

export const UserCard = ({ formMethods, cpf }: any) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;

  const [showMore, setShowMore] = useState(false);
  const { data: usersAllStore, isFetching: isFetchingOperators } = useGetOperatorsQuery(cpf);

  const onSubmit = (data: FormData) => {
    console.log(data);
  };

  const { data, isFetching: isFetchingLogix } = useFindUserInLogixQuery(cpf);

  const isLoading = isFetchingLogix || isFetchingOperators;

  formMethods.setValue("name", data?.name);
  formMethods.setValue("cpf", data?.cpf);
  formMethods.setValue("cost_center", data?.cost_center);
  formMethods.setValue("store", data?.company);
  formMethods.setValue("position", data?.position);
  formMethods.setValue("register", data?.register);
  formMethods.setValue("situation", data?.situation);

  return (
    <Card className="relative">
      {isLoading && (<CardLoadingSpinner />)}

      <CardHeader>
        <CardTitle>Usuário</CardTitle>
        <CardDescription>
          Dados do usuário que está solicitando o acesso
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="cpf" className="flex items-center gap-2">
                  CPF
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-black bg-opacity-75 text-white p-1 rounded shadow-lg">
                      <div className="relative">
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-black bg-opacity-75 rotate-45"></div>
                        Digite o CPF e aguarde a busca e validação do usuário
                        automática
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </Label>
                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value}
                      disabled
                      hasError={errors.cpf}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                      className="input-class"
                      placeholder="000.000.000-00"
                    />
                  )}
                />
                {errors.cpf && (
                  <p className="text-red-500 text-sm">{errors.cpf.message}</p>
                )}
              </div>
            </div>
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  type="text"
                  disabled={true}
                  value={data?.name}
                  placeholder="Nome Completo"
                  {...register("name")}
                  className="w-full"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="matricula">Matrícula</Label>
                <Input
                  id="matricula"
                  type="text"
                  disabled={true}
                  placeholder="000000"
                  value={data?.register}
                  {...register("matricula")}
                  className="w-full"
                />
                {errors.matricula && (
                  <p className="text-red-500 text-sm">
                    {errors.matricula.message}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="cargo">Cargo</Label>
                <Input
                  id="cargo"
                  type="text"
                  placeholder="Cargo"
                  value={data?.position}
                  disabled={true}
                  {...register("cargo")}
                  className="w-full"
                />
                {errors.cargo && (
                  <p className="text-red-500 text-sm">{errors.cargo.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="matricula">Situação</Label>
                <Input
                  id="situation"
                  type="text"
                  disabled={true}
                  placeholder=""
                  value={data?.situation}
                  {...register("situation")}
                  className="w-full"
                />
                {errors.situation && (
                  <p className="text-red-500 text-sm">
                    {errors.situation.message}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 w-1/2">
              <div className="flex-1 flex flex-col gap-1">
                <Label htmlFor="cost_center">Centro de Custo</Label>
                <Input
                  id="cost_center"
                  type="text"
                  placeholder=""
                  value={data?.cost_center}
                  disabled={true}
                  {...register("cost_center")}
                  className="w-full"
                />
                {errors.cost_center && (
                  <p className="text-red-500 text-sm">
                    {errors.cost_center.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{
              height: showMore ? "auto" : 0,
              opacity: showMore ? 1 : 0,
            }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            {showMore && (
              <>
                <div className="mt-4 mb-4">
                  <CardTitle>Histórico do Usuário</CardTitle>
                  <CardDescription>
                    Lojas e setores que o usuário está/esteve vinculado
                  </CardDescription>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {usersAllStore?.map((item: any, index: number) => (
                    <div key={index} className="mb-4 p-4 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-muted-foreground">
                          Últ. alt.:{" "}
                          {format(
                            new Date(item?.last_change_date),
                            "dd/MM/yyyy"
                          )}
                        </span>
                        <Badge
                          variant={
                            item?.status === "S" ? "destructive" : "approved"
                          }
                        >
                          {item?.status === "S" ? "Inativo" : "Ativo"}
                        </Badge>
                      </div>
                      <p className="text-sm mb-2">{item?.name}</p>
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-muted-foreground">
                          Lojas: Loja {item?.store_code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>

          <div className="flex justify-center mt-4">
            <Button
              type="button"
              onClick={() => setShowMore(!showMore)}
              className="border-none flex gap-4"
              variant="outline"
            >
              {showMore ? "Ver menos" : "Ver mais"}
              {showMore ? <FaChevronUp /> : <FaChevronDown />}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};